import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import {
  ArrowLeftIcon,
  CheckIcon,
  FileIcon,
  UploadCloudIcon,
} from '../components/icons';
import { formatBytes } from '../lib/utils';

type Stage = 'idle' | 'creating' | 'uploading' | 'building' | 'done' | 'error';

const STAGE_TEXT: Record<Stage, string> = {
  idle: '',
  creating: '正在创建应用…',
  uploading: '正在上传 ZIP 包…',
  building: '正在触发构建…',
  done: '完成，正在跳转…',
  error: '操作失败',
};

interface SubmitPayload {
  name: string;
  file: File;
}

export default function CreateApp() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [stage, setStage] = useState<Stage>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [dropError, setDropError] = useState('');

  const submit = useMutation({
    mutationFn: async ({ name: appName, file: zip }: SubmitPayload) => {
      setStage('creating');
      setErrorMsg('');
      const app = await api.createApp(appName);
      setStage('uploading');
      await api.uploadZip(app.id, zip);
      setStage('building');
      await api.triggerBuild(app.id);
      setStage('done');
      return app;
    },
    onSuccess: (app) => {
      queryClient.invalidateQueries({ queryKey: ['apps'] });
      navigate(`/apps/${app.id}`);
    },
    onError: (err) => {
      setStage('error');
      setErrorMsg((err as Error).message || '提交失败，请稍后重试。');
    },
  });

  const busy = stage === 'creating' || stage === 'uploading' || stage === 'building';
  const canSubmit = name.trim().length > 0 && file !== null && !busy;

  function pickFile(f: File | null | undefined) {
    setDropError('');
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.zip')) {
      setDropError('仅支持 .zip 格式的文件');
      return;
    }
    setFile(f);
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    pickFile(e.dataTransfer.files?.[0]);
  }

  function onDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(true);
  }

  function reset() {
    setStage('idle');
    setErrorMsg('');
  }

  function handleSubmit() {
    if (!canSubmit || !file) return;
    submit.mutate({ name: name.trim(), file });
  }

  return (
    <div className="page-wrap">
      <Link to="/" className="back-link">
        <ArrowLeftIcon size={16} />
        返回应用列表
      </Link>

      <h1 className="page-title-lg">创建新应用</h1>
      <p className="page-sub">上传你的项目 ZIP 包，Cloupak 将自动完成构建与打包。</p>

      <div className="form-card">
        <div className="form-group">
          <label className="form-label" htmlFor="app-name">
            应用名称 <span className="required">*</span>
          </label>
          <input
            id="app-name"
            className="form-input"
            placeholder="例如：my-awesome-app"
            value={name}
            maxLength={40}
            disabled={busy}
            onChange={(e) => setName(e.target.value)}
          />
          <div className="form-hint">这个名字会显示在应用图标下方（最多 40 字）。</div>
        </div>

        <div className="form-group">
          <label className="form-label">
            上传项目 ZIP 包 <span className="required">*</span>
          </label>
          <div
            className={`drop-zone${dragOver ? ' dragover' : ''}${file ? ' has-file' : ''}`}
            onDragOver={onDragOver}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => !busy && inputRef.current?.click()}
            role="button"
            tabIndex={0}
          >
            <input
              ref={inputRef}
              type="file"
              accept=".zip,application/zip"
              hidden
              disabled={busy}
              onChange={(e) => pickFile(e.target.files?.[0])}
            />
            {file ? (
              <div className="uploaded-file">
                <span className="uploaded-file-icon">
                  <CheckIcon size={16} />
                </span>
                <span className="uploaded-file-name" title={file.name}>{file.name}</span>
                <span className="uploaded-file-size">{formatBytes(file.size)}</span>
                {!busy && (
                  <button
                    type="button"
                    className="uploaded-file-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                  >
                    移除
                  </button>
                )}
              </div>
            ) : (
              <div className="drop-zone-empty">
                <div className="drop-icon">
                  <UploadCloudIcon size={32} />
                </div>
                <h3 className="drop-title">拖拽 ZIP 文件到此处</h3>
                <p>或点击选择文件</p>
                <span className="drop-hint">支持 .zip 格式 · 最大 50MB</span>
              </div>
            )}
          </div>
          {dropError && <div className="field-error">{dropError}</div>}
        </div>

        {busy && (
          <div className="progress-box">
            <div className="progress-bar">
              <div className="progress-bar-fill progress-bar-indeterminate" />
            </div>
            <p className="progress-text">{STAGE_TEXT[stage]}</p>
          </div>
        )}

        {stage === 'error' && (
          <div className="state-box state-error">
            <span>{STAGE_TEXT[stage]}：{errorMsg}</span>
            <button className="btn btn-ghost btn-sm" onClick={reset}>
              重置
            </button>
          </div>
        )}

        <div className="form-actions">
          <button
            className="btn btn-ghost btn-lg"
            disabled={busy}
            onClick={() => navigate('/')}
          >
            取消
          </button>
          <button
            className="btn btn-primary btn-lg"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {busy ? STAGE_TEXT[stage] : (<><FileIcon size={16} /> 上传并构建</>)}
          </button>
        </div>
      </div>
    </div>
  );
}
